import { useEffect, useReducer, useRef } from 'react'
import { reserveReducer, initialReserveState, TOTAL, validateStep, buildPayload, postReservation } from './reserveReducer'
import { useCalEmbedLoader, openCalModal } from '../../hooks/useCalEmbed'
import StepModel from './steps/StepModel'
import StepContact from './steps/StepContact'
import StepSegment from './steps/StepSegment'
import StepDetailsStartup from './steps/StepDetailsStartup'
import StepDetailsEnterprise from './steps/StepDetailsEnterprise'
import StepSpend from './steps/StepSpend'
import StepTimeline from './steps/StepTimeline'
import StepDone from './steps/StepDone'

export default function ReserveModal({ reportedBurn }) {
  const [state, dispatch] = useReducer(reserveReducer, initialReserveState)
  const { isOpen, step, answers, flashMsg } = state
  const flashTimeoutRef = useRef(null)
  // True only when the press that started this click landed on the backdrop
  // itself — so a text selection dragged out of the dialog doesn't close it.
  const pressedBackdropRef = useRef(false)

  useCalEmbedLoader()

  // Document-level delegated triggers, matching the original's
  // `document.querySelectorAll('[data-reserve]'/'[data-talk-to-us]')` wiring —
  // these buttons live scattered across many components (nav, hero, final CTA,
  // calculator CTA), so a single delegated listener is the lowest-risk port.
  useEffect(() => {
    function onClick(e) {
      if (e.target.closest('[data-reserve]')) {
        e.preventDefault()
        dispatch({ type: 'OPEN', reportedBurn })
      } else if (e.target.closest('[data-talk-to-us]')) {
        e.preventDefault()
        openCalModal()
      }
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [reportedBurn])

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
  }, [isOpen])

  useEffect(() => {
    function onKeydown(e) {
      if (e.key === 'Escape' && isOpen) dispatch({ type: 'CLOSE' })
    }
    document.addEventListener('keydown', onKeydown)
    return () => document.removeEventListener('keydown', onKeydown)
  }, [isOpen])

  useEffect(() => {
    if (flashMsg) {
      flashTimeoutRef.current = setTimeout(() => dispatch({ type: 'CLEAR_FLASH' }), 1600)
      return () => clearTimeout(flashTimeoutRef.current)
    }
  }, [flashMsg])

  function handleNext() {
    const message = validateStep(step, answers)
    const isLastInputStep = step === TOTAL - 1
    dispatch({ type: 'NEXT' }) // re-validates internally too; sets flashMsg or advances step
    if (!message && isLastInputStep) {
      // Relay the reservation to Slack/DB, then advance to StepDone, which sets
      // up the Cal.com booking embed. No email is opened.
      const payload = buildPayload(answers, reportedBurn)
      postReservation(payload)
    }
  }

  if (!isOpen) return null

  const isDone = step === TOTAL

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(17,17,17,.42)] p-6 backdrop-blur-[3px]"
      aria-hidden="false"
      onMouseDown={(e) => {
        pressedBackdropRef.current = e.target === e.currentTarget
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && pressedBackdropRef.current) dispatch({ type: 'CLOSE' })
      }}
    >
      <div
        className={`relative max-h-[92vh] w-full overflow-y-auto rounded-[18px] border border-[var(--line)] bg-[var(--paper)] shadow-[0_40px_120px_rgba(17,17,17,.25)] transition-[max-width] duration-250 ease-in-out ${
          isDone ? 'max-w-[960px]' : 'max-w-[520px]'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mTitle"
      >
        <button
          className="absolute top-5 right-[22px] flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-[var(--line)] bg-white text-[17px] leading-none text-[var(--muted)] transition-colors duration-150 hover:border-[var(--ink)] hover:text-[var(--ink)]"
          aria-label="Close"
          onClick={() => dispatch({ type: 'CLOSE' })}
        >
          &times;
        </button>
        <div className="px-[22px] pt-7 sm:px-8">
          <div className="font-mono text-[11.5px] tracking-[0.09em] text-[var(--ink-blue)] uppercase">Bring us your workload</div>
          <h3 id="mTitle" className="mt-3 text-2xl font-semibold tracking-[-0.02em]">Let's size your workload.</h3>
        </div>
        <div className="flex gap-1.5 px-[22px] pt-5 sm:px-8">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className={`h-1 flex-1 rounded-[3px] transition-colors duration-250 ${i <= Math.min(step, TOTAL - 1) ? 'bg-[var(--ink-blue)]' : 'bg-[var(--line)]'}`} />
          ))}
        </div>
        <div className="px-[22px] pt-[22px] pb-8 sm:px-8">
          <StepModel answers={answers} dispatch={dispatch} active={step === 0} />
          <StepContact answers={answers} dispatch={dispatch} active={step === 1} />
          <StepSegment answers={answers} dispatch={dispatch} active={step === 2} />
          {answers.segment === 'enterprise' ? (
            <StepDetailsEnterprise answers={answers} dispatch={dispatch} active={step === 3} />
          ) : (
            <StepDetailsStartup answers={answers} dispatch={dispatch} active={step === 3} />
          )}
          <StepSpend answers={answers} dispatch={dispatch} active={step === 4} />
          <StepTimeline answers={answers} dispatch={dispatch} active={step === 5} reportedBurn={reportedBurn} />
          <StepDone answers={answers} active={isDone} reportedBurn={reportedBurn} />

          {!isDone && (
            <div className="mt-[26px] flex items-center justify-between gap-3">
              <button
                className={`bg-transparent p-1.5 font-mono text-[13px] text-[var(--muted)] transition-colors duration-150 hover:text-[var(--ink)] ${step === 0 ? 'invisible' : ''}`}
                onClick={() => dispatch({ type: 'BACK' })}
              >
                &larr; Back
              </button>
              <button
                className="inline-flex items-center gap-2 rounded-[9px] px-6 py-[13px] font-mono text-sm font-medium text-[var(--paper)] transition-[background-color,transform,box-shadow] duration-150 hover:-translate-y-px"
                style={{ background: flashMsg ? '#B4341F' : 'var(--ink)' }}
                onClick={handleNext}
              >
                {flashMsg || (step === TOTAL - 1 ? 'Send reservation →' : 'Continue →')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
